import { useQuery } from "@tanstack/react-query";
import buildQuery, { Filter, guid } from "odata-query";
import { FilterOperation, Property } from "types";
import { GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import {Document} from "types/Document";
import { useWorkspace } from "./useWorkspace";
import { WorkspaceConfig } from "types/WorkspaceConfig";
import { useView, ViewConfig } from "./useView";
import { useEffect, useState } from "react";
import { axiosBeyonClient } from "utils/beyonAuth/beyonAxiosClient";
import { BatchResponse, useBatch } from "./useBatch";

const BATCH_THRESHOLD = 10;

export function useDocuments(workspaceId: string, viewId: string) {
    const [currentQuery, setCurrentQuery] = useState<DocumentsQuery>({
        tagFilter: [],
        selectionFilter: [],
        pageIndex: 0,
        pageSize: 25,
    });

    // Load the workspace config with the custom connector properties
    const workspace = useWorkspace(workspaceId);

    const workspaceConfig = workspace.data?.config;
    const connectorConfig = workspaceConfig?.connectorConfig;

    const viewConfig = useView(workspaceId, viewId)?.data;
    const { mutateAsync: fetchBatch } = useBatch<ODataArrayResponse<IndexableDocument>>(workspaceConfig);
    const odataQuery = useODataQuery(currentQuery, viewConfig, workspaceConfig);

    const enabledQuery = !!currentQuery && !!viewConfig && !!workspaceConfig && !!odataQuery;

    const { data, isError, isLoading, refetch } = useQuery({
        queryKey: ["documents", workspaceId, viewId],
        queryFn: async () => {
            const url = `${connectorConfig?.apiEndpoint}/${connectorConfig?.entity}`;
            const { pageIndex, pageSize, tagFilter = [], selectionFilter = [], filterModel } = currentQuery;

            // selection filters are split and batched if we surpass query size threshold
            if (currentQuery.selectionFilter.length > 1) {
                const batchRequests = selectionFilter.map(subFilter => {
                    const partial: DocumentsQuery = {
                        ...currentQuery,
                        selectionFilter: [subFilter]

                    };
                    const odata = buildBatchODataQuery(partial, viewConfig!, workspaceConfig!);
                    const qs = odata.startsWith("?") ? odata.slice(1) : odata;
                    const relativeUrl = `${connectorConfig?.entity}?${qs}`;
                    return {
                        method: "GET",
                        url: relativeUrl,
                        body: {}
                    };
                });

                const batchResponse = await fetchBatch(batchRequests);

                const reconstructedDocuments = batchResponse.responses
                    .flatMap(r => r.body.value)
                    .map(d => mapDocumentProperties(d, workspaceConfig!));

                const totalCount = batchResponse.responses
                    .reduce((sum, r) => sum + (r.body["@odata.count"] || 0), 0);

                return { documents: reconstructedDocuments as IndexableDocument[], count: totalCount };
            }

            const queryString = odataQuery.startsWith('?') ? odataQuery.slice(1) : odataQuery;

            const result = await axiosBeyonClient.get<ODataArrayResponse<IndexableDocument>>(`${url}?${queryString}`);
            const documents = result.data?.value?.map(doc => mapDocumentProperties(doc, workspaceConfig) as IndexableDocument);
            return {
                documents: documents,
                count: result.data["@odata.count"]
            };
        },
        enabled: enabledQuery
    });

    useEffect(() => {
        if (enabledQuery)
            refetch()
    }, [refetch, odataQuery, enabledQuery]);

    // Handle sort, filter, and pagination updates
    const handleSortChange = (newSortModel: GridSortModel) => {
        setCurrentQuery((prevQuery) => ({
            ...prevQuery,
            sortModel: newSortModel,
        }));
    };

    const handleTagFilterChange = (tags: string[]) => {
        if(!workspaceConfig?.tagConfig
            || !workspaceConfig?.tagConfig?.documentTagCollectionProperty
            || !workspaceConfig?.tagConfig?.tagValueProperty
            || !viewConfig?.gridConfig?.enableTags)
        {
            console.warn("Tag config is invalid or disabled. Skipping tag filter update.");
            return ;
        }

        setCurrentQuery((prevQuery) => ({
            ...prevQuery,
            tagFilter: tags.length === 0
                ? []
                : [{
                    [workspaceConfig?.tagConfig?.documentTagCollectionProperty]: {
                        any: {
                            or: tags.map(t => ({ [workspaceConfig?.tagConfig?.tagValueProperty]: t }))
                        }
                    }
                }],
        }));
    };

    const handleFilterChange = (newFilterModel: GridFilterModel) => {
        setCurrentQuery((prevQuery) => ({
            ...prevQuery,
            filterModel: newFilterModel,
        }));
    };

    const handlePageChange = (newPageIndex: number) => {
        setCurrentQuery((prevQuery) => ({
            ...prevQuery,
            pageIndex: newPageIndex,
        }));
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setCurrentQuery((prevQuery) => ({
            ...prevQuery,
            pageSize: newPageSize,
        }));
    };

    const handleSelectionFilterChange = (newSelection: string[]) => {
        const idField = workspaceConfig?.connectorConfig?.idProperty ?? "id";
        const selectionFilters = splitSelection(newSelection, idField, BATCH_THRESHOLD);
        setCurrentQuery((prevQuery) => ({
            ...prevQuery,
            selectionFilter: selectionFilters,
        }))
    
    };

    return {
        documents: data?.documents,
        count: data?.count,
        isLoading,
        isError,
        refetch,
        handleSortChange,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
        handleTagFilterChange,
        handleSelectionFilterChange,
        pageIndex: currentQuery.pageIndex,
        pageSize: currentQuery.pageSize,
        sortOrder: currentQuery.sortModel
    }
}

export type ODataResponse<T> = {
    value: T
    "@odata.context": string
}

export type ODataArrayResponse<T> = {
    value: T[]
    "@odata.count": number
}

export const documentProperties = [
    "id",
    "title",
    "type",
    "fileId",
    "size"
] as const;

export type MetadataDictionary = { [key: string]: string[] }

export type DocumentsQuery = {

    //TODO use the OData-query types here
    sortModel?: GridSortModel,
    filterModel?: GridFilterModel,
    selectionFilter?: Filter<any>[],
    tagFilter?: Filter<any>[],
    pageIndex: number,
    pageSize: number
}

export type IndexableDocument = Document & Record<string, any>;

export function useODataQuery(query: DocumentsQuery, viewConfig: ViewConfig, workspaceConfig: WorkspaceConfig) {

    const { sortModel: gridSortModel, filterModel: gridFilterModel, tagFilter, pageIndex, pageSize, selectionFilter } = query; // Grid filter model coming from the MUI grid

    const { orderBy: configOrderBy = [], filter: configFilters = [] } = viewConfig?.gridConfig ?? {};

    const propertyMapping = workspaceConfig ? getPropertyMapping(workspaceConfig) : null;
    const properties = workspaceConfig?.propertiesSchema  || [];

    const gridOrderBy = gridSortModel?.map(s => `${s.field === "size" ? propertyMapping?.size : s.field} ${s.sort ?? "asc"}`);

    return buildQuery({
        filter: [
            filterModelToODataFilter(gridFilterModel, properties),
            ...(selectionFilter || []),
            ...(tagFilter || []),
            ...configFilters
        ],
        expand: (workspaceConfig?.tagConfig?.documentTagCollectionProperty && viewConfig?.gridConfig?.enableTags) ? workspaceConfig.tagConfig.documentTagCollectionProperty : undefined,
        orderBy: gridOrderBy || configOrderBy,
        skip: pageIndex * pageSize,
        top: pageSize,
        count: true
    });
}

function filterModelToODataFilter(filter: GridFilterModel, properties: Property[]): Filter<any> {
    if (!filter?.items || filter.items.length === 0) return {};

    const getPropertyType = (field: string) => properties?.find(p => p.name === field)?.type;

    const mappedFilters = filter.items.map(item =>
        propertyToOdataFilter(
            item.columnField,
            item.operatorValue,
            item.value,
            getPropertyType(item.columnField)
        )
    );

    if (mappedFilters.length === 1) {
        return mappedFilters[0];
    }
    return {
        [filter.linkOperator ?? "and"]: mappedFilters
    };
}

function propertyToOdataFilter(property: string, operator: string, value: string | string[], propertyType: string) {
    const op = operator.toLowerCase();
    switch (propertyType) {
        case "string":
        case "text": {
            switch (op) {
                case FilterOperation.Contains.toLowerCase():
                    return { [property]: { contains: value } };
                case FilterOperation.Equals.toLowerCase():
                    return { [property]: value };
                case FilterOperation.StartsWith.toLowerCase():
                    return { [property]: { startswith: value } };
                case FilterOperation.EndsWith.toLowerCase():
                    return { [property]: { endswith: value } };
                case FilterOperation.IsEmpty.toLowerCase():
                    return { [property]: "" };
                case FilterOperation.IsNotEmpty.toLowerCase():
                    return { [property]: { ne: "" } };
                case FilterOperation.IsAnyOf.toLowerCase():
                    return { [property]: { in: Array.isArray(value) ? value : [value] } };
                default:
                    return {};
            }
        }
        case "number":
        case "integer": {
            const numVal = Array.isArray(value) ? value.map(Number) : Number(value);
            switch (op) {
                case FilterOperation.NumericEquals.toLowerCase():
                    return { [property]: numVal };
                case FilterOperation.NumericNotEqual.toLowerCase():
                    return { [property]: { ne: numVal } };
                case FilterOperation.Greater.toLowerCase():
                    return { [property]: { gt: numVal } };
                case FilterOperation.GreaterOrEqual.toLowerCase():
                    return { [property]: { ge: numVal } };
                case FilterOperation.Lower.toLowerCase():
                    return { [property]: { lt: numVal } };
                case FilterOperation.LowerOrEqual.toLowerCase():
                    return { [property]: { le: numVal } };
                case FilterOperation.IsEmpty.toLowerCase():
                    return { [property]: null };
                case FilterOperation.IsNotEmpty.toLowerCase():
                    return { [property]: { ne: null } };
                case FilterOperation.IsAnyOf.toLowerCase():
                    return { [property]: { in: Array.isArray(numVal) ? numVal : [numVal] } };
                default:
                    return {};
            }
        }
        case "boolean": {
            if (op === FilterOperation.Is.toLowerCase()) {
                if (value === "any") return {};
                if (value === "true") return { [property]: true };
                if (value === "false") return { or: [
                            { [property]: false },
                            { [property]: null}
                        ]};
            }
            return {};
        }
        case "dateTime":
        case "date":
        case "time": {
            const dateVal = new Date(value as string);
            if (isNaN(dateVal.getTime())) return {};
            switch (op) {
                case FilterOperation.After.toLowerCase():
                    return { [property]: { gt: dateVal } };
                case FilterOperation.OnOrAfter.toLowerCase():
                    return { [property]: { ge: dateVal } };
                case FilterOperation.Before.toLowerCase():
                    return { [property]: { lt: dateVal } };
                case FilterOperation.OnOrBefore.toLowerCase():
                    return { [property]: { le: dateVal } };
                case FilterOperation.Is.toLowerCase(): {
                    // Use local timezone instead of UTC
                    const startOfDay = new Date(dateVal);
                    startOfDay.setHours(0, 0, 0, 0); // Start of local day
                    
                    const endOfDay = new Date(dateVal);
                    endOfDay.setHours(23, 59, 59, 999); // End of local day
                    
                    return { [property]: { ge: startOfDay, le: endOfDay } };
                }
                case FilterOperation.IsNot.toLowerCase(): {
                    // Use local timezone instead of UTC
                    const startOfDay = new Date(dateVal);
                    startOfDay.setHours(0, 0, 0, 0); // Start of local day
                    
                    const endOfDay = new Date(dateVal);
                    endOfDay.setHours(23, 59, 59, 999); // End of local day
                    
                    return {
                        or: [
                            { [property]: { lt: startOfDay } },
                            { [property]: { gt: endOfDay } }
                        ]
                    };
                }
                default:
                    return {};
            }
        }
    }
}

export function getPropertyMapping(config: WorkspaceConfig) {
    return {
        id : config.connectorConfig.idProperty,
        title : config.connectorConfig.titleProperty,
        name : config.connectorConfig.fileNameProperty,
        type : config.connectorConfig.typeProperty,
        fileId : config.connectorConfig.fileIdProperty,
        size : config.connectorConfig.fileSizeProperty
    };
}

export function mapDocumentProperties(document: Partial<IndexableDocument>, config: WorkspaceConfig) {
    const propertyMapping = getPropertyMapping(config);

    // keep newly introduced columns by view config while mapping the standard columns
    const mappedDocument = { ...document };

    mappedDocument.id = document[propertyMapping.id];
    mappedDocument.title = document[propertyMapping.title];
    mappedDocument.name = document[propertyMapping.name];
    mappedDocument.type = document[propertyMapping.type];
    mappedDocument.fileId = document[propertyMapping.fileId];
    mappedDocument.size = document[propertyMapping.size];

    if (config.tagConfig?.documentTagCollectionProperty)
        mappedDocument.tags = (document[config.tagConfig?.documentTagCollectionProperty] || []).map((x: Record<string, string>) => ({
            value: x[config.tagConfig?.tagValueProperty],
            documentId: x[config.tagConfig?.tagDocumentIdProperty]
        }));

    return mappedDocument;
}

export function buildBatchODataQuery(query: DocumentsQuery,
    viewConfig: ViewConfig,
    workspaceConfig: WorkspaceConfig
): string {
    const {
        sortModel: gridSortModel,
        filterModel: gridFilterModel,
        tagFilter,
        pageIndex,
        pageSize,
        selectionFilter
    } = query;

    const { orderBy: configOrderBy = [], filter: configFilters = [] } =
        viewConfig.gridConfig ?? {};

    const propertyMapping = getPropertyMapping(workspaceConfig);
    const gridOrderBy =
        gridSortModel?.map(
            (s) =>
                `${s.field === "size" ? propertyMapping.size : s.field} ${s.sort ?? "asc"}`
        ) ?? configOrderBy;

    return buildQuery({
        filter: [
            filterModelToODataFilter(gridFilterModel, workspaceConfig.propertiesSchema),
            ...(selectionFilter ?? []),
            ...(tagFilter ?? []),
            ...configFilters
        ],
        expand:
            workspaceConfig.tagConfig?.documentTagCollectionProperty &&
                viewConfig.gridConfig.enableTags
                ? workspaceConfig.tagConfig.documentTagCollectionProperty
                : undefined,
        orderBy: gridOrderBy,
        skip: pageIndex * pageSize,
        top: pageSize,
        count: true
    });
}
export function splitSelection(selection: string[], idField: string, size: number){
    // if selected documents is large, we run into odata node query size limit
    // so we split the selection into smaller chunks and batch the query
    const chunks: string[][] = [];
    for (let i = 0; i < selection.length; i += size){
        chunks.push(selection.slice(i, i + size));
    }
    
    const selectionFilters = selection.length === 0
        ? []
        : chunks.map(batch =>
        ({
            or: batch.map(id => ({
                [idField]: { eq: guid(id) }
            }))
        })
        );
    return selectionFilters;
}
